import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon } from "lucide-react";
import type { RegisterUserType } from "@/domain/core";
import type { UserInfo } from "@/types/core";
import { cn, layout } from "@/utilities/tailwind-utils";

// ============================================================================
// USER TYPE SELECTION TYPES
// ============================================================================

interface UserTypeSelectionProps {
  onSelect: (userType: RegisterUserType) => void;
  onCancel: () => void;
  userInfo: UserInfo;
}

// ============================================================================
// USER TYPE SELECTION COMPONENT
// ============================================================================

export function UserTypeSelection({
  onSelect,
  onCancel,
  userInfo
}: UserTypeSelectionProps) {
  return (
    <div className="space-y-6">
      {/* Account Type Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        {/* Choose Your Account Type Section Title */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Choose Your Account Type</h3>
          <p className="text-sm text-muted-foreground">
            Select the type of account that best describes you. This will help
            us customize your experience.
          </p>
        </div>

        {/* Account Information */}
        <div className="flex items-start">
          <div className="p-3 bg-muted/50 rounded-md w-full">
            <p className="text-sm text-muted-foreground mb-1">
              Account Information:
            </p>
            <div className="text-sm">
              <span className="font-medium w-12 inline-block">Name</span>{" "}
              {userInfo.name}
              <br />
              <span className="font-medium w-12 inline-block">Email</span>{" "}
              {userInfo.email}
            </div>
          </div>
        </div>
      </div>

      <div className={cn(layout.gridCols1MdCols2)}>
        {/* Professional Option */}
        <Card
          className="pt-0 pb-0 cursor-pointer hover:border-primary/50 transition-colors rounded-2xl"
          onClick={() => onSelect("professional")}
        >
          <CardContent className="p-3">
            <div className="flex flex-col items-stretch gap-4">
              {/* Professional Image */}
              <div className="flex-1">
                <img
                  src="/assets/images/account_professional.png"
                  alt="Healthcare Professional"
                  className="object-contain mx-auto"
                />
              </div>
              {/* Professional Features */}
              <ul className="flex-1 text-xs text-muted-foreground grid grid-cols-2 gap-2">
                <li className="flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                  Find and apply for shifts
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                  Manage your schedule
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                  Track earnings and payments
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                  Build your professional profile
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Practice Option */}
        <Card
          className="pt-0 pb-0 cursor-pointer hover:border-primary/50 transition-colors rounded-2xl"
          onClick={() => onSelect("practice")}
        >
          <CardContent className="p-3">
            <div className="flex flex-col items-stretch gap-4">
              {/* Practice Image */}
              <div className="flex-1">
                <img
                  src="/assets/images/account_practice.png"
                  alt="Healthcare Practice"
                  className="object-contain mx-auto"
                />
              </div>
              {/* Practice Features */}
              <ul className="flex-1 text-xs text-muted-foreground grid grid-cols-2 gap-2">
                <li className="flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                  Post job opportunities
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                  Review applications
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                  Manage staffing needs
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                  Track hiring analytics
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Information */}
      <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg mb-0">
        <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
          Need Help Choosing?
        </h5>
        <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
          <p>
            <strong>Professional:</strong> Choose this if you're a nurse,
            doctor, therapist, or other healthcare worker looking for employment
            opportunities.
          </p>
          <p>
            <strong>Practice:</strong> Choose this if you represent a hospital,
            clinic, nursing home, or other healthcare facility looking to hire
            staff.
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
